/* ═══════════════════════════════════════════════════════════════
   Forma designer tooltip copy — ONE place, so the wording is
   reviewable in one read.

   Voice (Josh, 2026-08-05: "make it dummy proof"):
     · plain English, ONE sentence
     · say what the control DOES, in Word terms a non-Word-nerd gets
     · no jargon without a gloss, no "configure the…" filler
   Rendered by <Hint> (components/ui/tooltip.tsx) — never `title=`.
   ═══════════════════════════════════════════════════════════════ */

export const FORMA_TIPS = {
  // ── Top bar / workspace ──
  formaName: 'The name this forma shows up under in your Master Formas library.',
  formaClient: 'Attach this forma to a client so their projects pick it by default.',
  exportToProject:
    'Copy this master into a project — the project gets its own separate forma and the master stays untouched.',
  dockLocked: 'Locked keeps the workspace tidy; unlock it to add, move, resize, or hide palettes.',
  dockUnlocked: 'Lock the workspace back down so palettes stop moving while you work.',
  resetLayout: 'Put every palette back where it started, at its original size.',
  exportDotx: 'Save this forma as a Word template (.dotx) you can open or hand to someone.',
  save: 'Save your changes to this forma.',
  history: 'See who changed this forma and when.',

  // ── Typography ──
  bodyFont: 'The typeface every paragraph of the spec is set in.',
  bodyFontSize: 'How big the body text is, in points — 10 pt is the usual spec size.',
  lineSpacing: 'How far apart the lines inside one paragraph sit — 1 is single-spaced.',

  // ── Margins ──
  marginTop: 'White space between the top of the page and the header.',
  marginBottom: 'White space between the footer and the bottom of the page.',
  marginLeft: 'White space down the left edge of the page.',
  marginRight: 'White space down the right edge of the page.',

  // ── Formats ──
  specNumberFormat:
    'How section numbers are punctuated — # stands for a digit, so ## ## ## prints 22 14 00.',
  dateFormat: 'How every date in this forma is written out.',

  // ── Layout Options ──
  firstPageDifferent: 'Give page 1 its own header and footer, different from the rest.',
  oddEvenDifferent:
    'Give left-hand and right-hand pages their own headers and footers — for double-sided printing.',
  widowOrphan:
    'Stops a single line of a paragraph being left alone at the top or bottom of a page.',
  pageVariantSwitcher:
    'Pick which page the canvas is editing — each one has its own header/footer.',

  // ── Watermark ──
  watermark:
    'Prints faint diagonal text across every page, like DRAFT or NOT FOR CONSTRUCTION. Leave it blank for none.',

  // ── Section End ──
  eosEnabled: 'Print a closing line — usually "END OF SECTION" — at the end of every spec.',
  eosSpacing: 'How much empty space sits between the last paragraph and the closing line.',
  eosEditText: 'Change the closing wording, e.g. "END OF SECTION 22 14 00".',
  postBodyEnabled:
    'Adds three extra text slots after the body and before the closing line — most specs never need them.',
  blankPageEnabled: 'Add a blank "this page intentionally left blank" page at the end.',
  blankPageCondition:
    'Decide when that blank page is added — always, or only to even out the page count.',
  blankPageEditText: 'Change the wording printed on the blank page.',

  // ── Level Properties ──
  levelBold: 'Make this level’s text bold.',
  levelItalic: 'Slant this level’s text.',
  levelUnderline: 'Draw a line under this level’s text.',
  levelCaseUpper: 'PRINT THIS LEVEL IN CAPITALS.',
  levelCaseTitle: 'Print This Level With Each Word Capitalized.',
  levelCaseSentence: 'Print this level like a sentence — first word capitalized only.',
  levelFont: 'Give this level its own font and size. Off = follows Typography.',
  levelColor: 'The ink color for this level’s text.',
  levelAlignLeft: 'Line this level up against the left margin.',
  levelAlignCenter: 'Center this level between the margins.',
  levelAlignRight: 'Line this level up against the right margin.',
  levelAlignJustify: 'Stretch each full line so both edges are flush, like a newspaper column.',
  levelIndent: 'How far the whole paragraph sits in from the left margin, in inches.',
  levelHanging: 'How far the text sits from the number. 0 wraps text under the number.',
  levelSpaceBefore: 'Empty space added above this paragraph, in points.',
  levelSpaceAfter: 'Empty space added below this paragraph, in points.',
  levelKeepWithNext:
    'Never leave this heading stranded at the bottom of a page — it moves to the next page with its content.',
  levelKeepTogether:
    'Never split this paragraph across two pages — it moves whole to the next one.',
  levelNoSpaceSame:
    'When two paragraphs of this same level sit next to each other, skip the usual gap between them.',
  levelNumbering: 'Which numbering style this level uses, e.g. 1.01 or 1.1.',

  // ── Canvas affordances ──
  canvasIndentGrip: 'Drag to move this level in or out from the left margin.',
  canvasHangingGrip: 'Drag to set where wrapped text lines up under the number.',
  canvasSpaceBefore: 'Drag to set the space above this level.',
  canvasSpaceAfter: 'Drag to set the space below this level.',
  canvasSpaceSame: 'Drag to set the gap between two paragraphs of this same level.',
  canvasZoneSpacing: 'Drag to move this whole strip up or down the page.',
  canvasZoneTag: 'Click to select this strip — everything you drop here prints in that spot.',
  canvasEmptySlot: 'Drop a token here, or right-click to paste.',

  // ── Tokens palette ──
  tokenFields:
    'Placeholders Word fills in as it prints — page numbers, the section number, today’s date.',
  tokenProject: 'Fills in from the project the spec belongs to when you export it.',
  tokenElements: 'Spacing and decoration you drop between other tokens.',
  tokenCustom: 'Text you type yourself. Pin one to keep it here for next time.',
  tokenPlaceholder:
    'A blank you fill in per project — drop it now, decide what it means when the forma reaches a project.',
  tokenIssuanceDate:
    'The date of the issuance you are exporting, written in this forma’s date format.',

  // ── Project placeholders / bindings ──
  placeholderChip:
    'A per-project blank — set what it means in the project’s Project Tokens palette.',
  bindingKind:
    'Where this blank gets its text: a project field, a specific issuance, or text you type.',
  bindingField: 'Which piece of project information fills this blank.',
  bindingText: 'Type exactly what should print here for this project.',
  bindingIssuance: 'Which issuance this blank follows — its date and name come from that issuance.',
  bindingPreview: 'What will actually print in the exported document.',
  bindingWas: 'What the imported template had in this spot — kept as a reminder, never printed.',
} as const;

export type FormaTipKey = keyof typeof FORMA_TIPS;
